import React, { useEffect, useRef, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  ButtonGroup,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CenterFocusStrong,
  FitScreen
} from '@mui/icons-material';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { positionInterpolator } from '../../utils/positionInterpolator';

// Utility function to format time ago (like Facebook posts)
const formatTimeAgo = (timestamp: string | Date): string => {
  const now = new Date();
  const lastUpdate = new Date(timestamp);
  const diffInSeconds = Math.floor((now.getTime() - lastUpdate.getTime()) / 1000);

  if (diffInSeconds < 5) return 'just now';
  if (diffInSeconds < 60) return `${diffInSeconds}s ago`;

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d ago`;
};

// Utility function to determine tracker status based on data freshness
const getTrackerStatus = (lastUpdate: string | Date, originalStatus?: string): 'active' | 'standby' | 'offline' => {
  const now = new Date();
  const lastUpdateTime = new Date(lastUpdate);
  const diffInMinutes = Math.floor((now.getTime() - lastUpdateTime.getTime()) / (1000 * 60));

  // If more than 30 minutes without data, consider offline
  if (diffInMinutes >= 30) {
    return 'offline';
  }

  // If more than 5 minutes without data, consider standby
  if (diffInMinutes >= 5) {
    return 'standby';
  }

  // Recent data, keep as active
  return 'active';
};

interface SpeedMetrics {
  current: number;
  average: number;
  max: number;
  smoothed: number;
}

interface ElevationMetrics {
  current: number;
  gain: number;
  loss: number;
  gradient: number;
  isClimbing: boolean;
}

interface TrackerData {
  trackerId: string;
  currentRole: string;
  title: string;
  position: {
    lat: number;
    lng: number;
  };
  speed: SpeedMetrics | number;
  elevation: ElevationMetrics | number;
  battery?: number;
  batteryLevel?: number;
  signalBars?: number;
  signalStrength?: number;
  networkType?: string;
  lastUpdate: string;
  status: 'active' | 'standby' | 'offline';
}

interface MapData {
  bounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  center?: {
    lat: number;
    lng: number;
  };
  zoom?: number;
}

interface ActiveRoute {
  raceId: string;
  totalDistance: number;
  raceName?: string;
  stageName?: string;
  hasCoordinates?: boolean;
}

interface LiveGPSMapProps {
  trackers: TrackerData[];
  mapData?: MapData;
  activeRoute?: ActiveRoute;
  height?: string | number;
}

// Set Mapbox access token
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

// Helper functions to extract values from metrics
const getSpeedValue = (speed: SpeedMetrics | number): number => {
  return typeof speed === 'object' ? speed.current : speed;
};

const getElevationValue = (elevation: ElevationMetrics | number): number => {
  return typeof elevation === 'object' ? elevation.current : elevation;
};

// Create pulsing dot exactly like Mapbox sample
const createPulsingDot = (color: string, size: number = 200) => {
  const pulsingDot = {
    width: size,
    height: size,
    data: new Uint8Array(size * size * 4),
    context: null as CanvasRenderingContext2D | null,

    onAdd: function () {
      const canvas = document.createElement('canvas');
      canvas.width = this.width;
      canvas.height = this.height;
      this.context = canvas.getContext('2d');
    },

    render: function () {
      const duration = 1000;
      const t = (performance.now() % duration) / duration;

      const radius = (size / 2) * 0.3;
      const outerRadius = (size / 2) * 0.7 * t + radius;
      const context = this.context;

      if (!context) return false;

      // Clear the canvas
      context.clearRect(0, 0, this.width, this.height);

      // Draw the outer circle
      context.beginPath();
      context.arc(
        this.width / 2,
        this.height / 2,
        outerRadius,
        0,
        Math.PI * 2
      );
      // Convert hex color to rgba with fading
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      context.fillStyle = `rgba(${r}, ${g}, ${b}, ${1 - t})`;
      context.fill();

      // Draw the inner circle
      context.beginPath();
      context.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2);
      context.fillStyle = color;
      context.strokeStyle = 'white';
      context.lineWidth = 3 + 6 * (1 - t);
      context.fill();
      context.stroke();

      // Update this image's data with data from the canvas
      this.data = new Uint8Array(context.getImageData(0, 0, this.width, this.height).data);

      // Continuously repaint the map, resulting in the smooth animation of the dot
      return true;
    }
  };

  return pulsingDot;
};

// Create start/finish marker element for routes
const createRouteMarkerElement = (type: 'start' | 'finish'): HTMLElement => {
  const el = document.createElement('div');
  const circleColor = type === 'start' ? '#22C55E' : '#000000'; // Green for start, black for finish

  el.style.cssText = `
    background-color: ${circleColor};
    border: 3px solid white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 12px;
    font-weight: bold;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    cursor: pointer;
    user-select: none;
  `;
  el.textContent = type === 'start' ? 'S' : 'F';
  return el;
};



const LiveGPSMap: React.FC<LiveGPSMapProps> = ({ trackers, mapData, activeRoute, height = '100%' }) => {
  console.log('LiveGPSMap: Component props received:', {
    hasActiveRoute: !!activeRoute,
    activeRoute: activeRoute,
    trackersCount: trackers?.length || 0,
    hasMapData: !!mapData
  });

  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const routeMarkers = useRef<{ start?: mapboxgl.Marker; finish?: mapboxgl.Marker }>({});

  // Camera control state
  const [autoCameraMode, setAutoCameraMode] = useState(true);
  const [followTracker, setFollowTracker] = useState<string>('all'); // 'all' or specific trackerId
  const [showControls, setShowControls] = useState(false);

  // Position interpolation for smooth movement
  const [interpolatedPositions, setInterpolatedPositions] = useState<Map<string, { lat: number; lng: number }>>(new Map());

  // Initialize position interpolator
  useEffect(() => {
    positionInterpolator.setUpdateCallback((positions) => {
      setInterpolatedPositions(new Map(positions));
    });

    return () => {
      positionInterpolator.clear();
    };
  }, []);

  // Memoize the route data to prevent unnecessary re-renders from WebSocket updates
  const stableRouteId = useMemo(() => {
    return activeRoute?.raceId || null;
  }, [activeRoute?.raceId]);

  const stableRouteData = useMemo(() => ({
    raceName: activeRoute?.raceName,
    stageName: activeRoute?.stageName,
    totalDistance: activeRoute?.totalDistance,
    hasCoordinates: activeRoute?.hasCoordinates
  }), [activeRoute?.raceName, activeRoute?.stageName, activeRoute?.totalDistance, activeRoute?.hasCoordinates]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    const initializeMap = () => {
      if (!mapContainer.current) {
        setTimeout(initializeMap, 100);
        return;
      }

      try {
        map.current = new mapboxgl.Map({
          container: mapContainer.current!,
          style: 'mapbox://styles/wedobroadcast/cmftsafww00j801sd8ifhcmir',
          center: [101.9758, 4.2105], // Center of Malaysia
          zoom: 6,
          attributionControl: false,
        });

        map.current.on('load', () => {
          console.log('Mapbox map loaded successfully');

          // Add pulsing dot images for each tracker status
          const statusColors = {
            active: '#3b82f6',    // Blue
            standby: '#f59e0b',   // Amber
            offline: '#94a3b8'    // Gray
          };

          Object.entries(statusColors).forEach(([status, color]) => {
            const pulsingDot = createPulsingDot(color, 200);
            map.current!.addImage(`beacon-${status}`, pulsingDot, { pixelRatio: 2 });
          });

          // Add source for tracker points
          map.current!.addSource('trackers', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: []
            }
          });

          // Add layer for tracker symbols
          map.current!.addLayer({
            id: 'tracker-symbols',
            type: 'symbol',
            source: 'trackers',
            layout: {
              'icon-image': ['get', 'icon'],
              'icon-size': 0.6, // Larger size for more visible beacons
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
              'text-field': ['get', 'label'],
              'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
              'text-size': 12,
              'text-offset': [0, 2.2], // Adjust offset for larger beacon
              'text-anchor': 'top',
              'text-allow-overlap': true, // Allow text labels to overlap
              'text-ignore-placement': true, // Always show text, ignore placement rules
              'text-optional': true // Icons still show even if text is hidden
            },
            paint: {
              'text-color': '#000000',
              'text-halo-width': 0
            }
          });

          // Pulsing dots handle their own animation via render() function
        });

      } catch (error) {
        console.error('Error initializing map:', error);
      }
    };

    initializeMap();

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Update trackers when data changes
  useEffect(() => {
    if (!map.current || !map.current.isStyleLoaded()) return;

    // Create GeoJSON features for all trackers with computed status
    const features = trackers
      .filter(tracker => tracker.position && tracker.position.lat && tracker.position.lng)
      .map(tracker => {
        // Compute actual status based on data freshness
        const computedStatus = tracker.lastUpdate
          ? getTrackerStatus(tracker.lastUpdate, tracker.status)
          : 'offline';

        // Feed position to interpolator for smooth animation
        const speed = getSpeedValue(tracker.speed || 0);
        positionInterpolator.updateTrackerPosition(
          tracker.trackerId,
          { lat: tracker.position.lat, lng: tracker.position.lng },
          speed
        );

        return {
          ...tracker,
          status: computedStatus
        };
      })
      .filter(tracker => tracker.status !== 'offline') // Hide offline trackers from map
      .map(tracker => {
        // Use interpolated position if available, otherwise fall back to actual position
        const interpolatedPos = interpolatedPositions.get(tracker.trackerId);
        const position = interpolatedPos || tracker.position;

        return {
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: [position.lng, position.lat]
          },
          properties: {
            trackerId: tracker.trackerId,
            icon: `beacon-${tracker.status}`,
            label: (tracker.title || tracker.currentRole).replace(/_/g, ' ').toUpperCase(),
            status: tracker.status,
            speed: getSpeedValue(tracker.speed || 0),
            elevation: getElevationValue(tracker.elevation || 0),
            battery: tracker.batteryLevel || 0,
            signal: tracker.signalStrength || 0,
            lastUpdate: tracker.lastUpdate
          }
        };
      });

    // Update the tracker source with new data
    const source = map.current.getSource('trackers');
    if (source && source.type === 'geojson') {
      (source as mapboxgl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features
      });
    }

    // Add click handler for popups (only add once)
    if (!(map.current as any).hasClickHandler) {
      map.current.on('click', 'tracker-symbols', (e) => {
        if (e.features && e.features.length > 0) {
          const feature = e.features[0];
          const coordinates = (feature.geometry as any).coordinates.slice();
          const properties = feature.properties!;

          // Create popup content
          const popupContent = `
            <div style="width: 280px; padding: 12px; font-family: Roboto, sans-serif; background-color: #1a1a1a; color: #fff; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.5);">
              <h3 style="margin: 0 0 8px 0; font-size: 1.1rem; font-weight: 600; color: #fff;">
                ${properties.label}
              </h3>
              <p style="margin: 0 0 12px 0; font-size: 0.75rem; color: #b0b0b0;">
                ID: ${properties.trackerId}
              </p>
              <div style="margin-bottom: 8px;">
                <span style="font-size: 0.85rem; color: #fff;">
                  📍 ${coordinates[1].toFixed(4)}, ${coordinates[0].toFixed(4)}
                </span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                <span style="font-size: 0.85rem; color: #fff;">⚡ ${properties.speed.toFixed(1)} km/h</span>
                <span style="font-size: 0.85rem; color: #fff;">⛰️ ${properties.elevation.toFixed(0)}m</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin-bottom: 12px;">
                <span style="font-size: 0.85rem; color: #fff;">🔋 ${properties.battery}%</span>
                <span style="font-size: 0.85rem; color: #fff;">📶 ${properties.signal}/5</span>
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="background-color: ${properties.status === 'active' ? '#22c55e' : properties.status === 'standby' ? '#f59e0b' : '#ef4444'}; color: white; padding: 4px 8px; border-radius: 12px; font-size: 0.7rem; text-transform: uppercase; font-weight: 600;">
                  ${properties.status}
                </span>
                <span style="font-size: 0.7rem; color: #b0b0b0;">
                  ${formatTimeAgo(properties.lastUpdate)}
                </span>
              </div>
            </div>
          `;

          new mapboxgl.Popup({
            className: 'dark-popup'
          })
            .setLngLat(coordinates)
            .setHTML(popupContent)
            .addTo(map.current!);
        }
      });

      // Change cursor on hover
      map.current.on('mouseenter', 'tracker-symbols', () => {
        if (map.current) map.current.getCanvas().style.cursor = 'pointer';
      });

      map.current.on('mouseleave', 'tracker-symbols', () => {
        if (map.current) map.current.getCanvas().style.cursor = '';
      });

      (map.current as any).hasClickHandler = true;
    }

    // Auto camera movement (only when auto mode is enabled)
    if (autoCameraMode && trackers.length > 0) {
      const validPositions = trackers
        .filter(t => t.position && t.position.lat && t.position.lng)
        .map(t => [t.position.lng, t.position.lat] as [number, number]);

      if (validPositions.length > 0) {
        if (followTracker === 'all') {
          // Follow all trackers - fit bounds
          const bounds = new mapboxgl.LngLatBounds();
          validPositions.forEach(pos => bounds.extend(pos));

          if (validPositions.length === 1) {
            // Single tracker - smoothly follow the tracker
            map.current.easeTo({
              center: validPositions[0],
              zoom: Math.max(map.current.getZoom(), 13), // Keep current zoom if higher than 13
              duration: 1000 // Smooth 1-second transition
            });
          } else {
            // Multiple trackers - fit bounds with smooth transition
            map.current.fitBounds(bounds, {
              padding: 50,
              maxZoom: 15,
              duration: 1000 // Smooth 1-second transition
            });
          }
        } else {
          // Follow specific tracker
          const specificTracker = trackers.find(t => t.trackerId === followTracker);
          if (specificTracker && specificTracker.position) {
            map.current.easeTo({
              center: [specificTracker.position.lng, specificTracker.position.lat],
              duration: 1000 // Smooth transition without zoom changes
            });
          }
        }
      }
    }
  }, [trackers, autoCameraMode, followTracker]);

  // Handle map data updates (bounds, center, zoom)
  useEffect(() => {
    if (!map.current || !mapData) return;

    if (mapData.bounds && trackers.length > 0) {
      const bounds = new mapboxgl.LngLatBounds([
        [mapData.bounds.west, mapData.bounds.south],
        [mapData.bounds.east, mapData.bounds.north]
      ]);
      map.current.fitBounds(bounds, { padding: 20 });
    } else if (mapData.center) {
      map.current.flyTo({
        center: [mapData.center.lng, mapData.center.lat],
        zoom: mapData.zoom || 13
      });
    }
  }, [mapData, trackers.length]);

  // Handle active route display - only reload if route ID changes
  useEffect(() => {
    console.log('LiveGPSMap: Route useEffect triggered', {
      stableRouteId,
      hasMap: !!map.current,
      hasCoordinates: stableRouteData.hasCoordinates
    });

    if (!map.current) {
      console.log('LiveGPSMap: No map instance, skipping route loading');
      return;
    }

    const routeSourceId = 'active-route';
    const routeLayerId = 'active-route-layer';

    // Remove existing route if any
    if (map.current.getLayer(routeLayerId)) {
      map.current.removeLayer(routeLayerId);
    }
    if (map.current.getSource(routeSourceId)) {
      map.current.removeSource(routeSourceId);
    }

    // Remove existing route markers
    if (routeMarkers.current.start) {
      routeMarkers.current.start.remove();
      routeMarkers.current.start = undefined;
    }
    if (routeMarkers.current.finish) {
      routeMarkers.current.finish.remove();
      routeMarkers.current.finish = undefined;
    }

    // Load route coordinates separately for performance
    if (stableRouteId && stableRouteData.hasCoordinates) {
      console.log('LiveGPSMap: Loading route coordinates for', stableRouteId);

      const loadRouteCoordinates = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/dashboard/active-route-coordinates`);
          if (!response.ok) {
            console.warn('Failed to load route coordinates:', response.statusText);
            return;
          }

          const result = await response.json();
          if (result.success && result.data.coordinates) {
            const coordinates = result.data.coordinates;
            console.log('LiveGPSMap: Rendering route with', coordinates.length, 'coordinates');

            const routeGeoJSON = {
              type: 'Feature' as const,
              properties: {
                name: stableRouteData.raceName || 'Active Route',
                stage: stableRouteData.stageName || '',
                distance: stableRouteData.totalDistance
              },
              geometry: {
                type: 'LineString' as const,
                coordinates: coordinates.map((coord: any) => [coord.lng, coord.lat])
              }
            };

            if (map.current) {
              map.current.addSource(routeSourceId, {
                type: 'geojson',
                data: routeGeoJSON
              });

              map.current.addLayer({
                id: routeLayerId,
                type: 'line',
                source: routeSourceId,
                layout: {
                  'line-join': 'round',
                  'line-cap': 'round'
                },
                paint: {
                  'line-color': '#EF4444', // Red route line
                  'line-width': 3,
                  'line-opacity': 1.0
                }
              });

              console.log(`Route displayed: ${stableRouteData.raceName} with ${coordinates.length} points`);

              // Add start and finish markers
              if (coordinates.length > 0) {
                const startCoord = coordinates[0];
                const finishCoord = coordinates[coordinates.length - 1];

                // Create start marker
                const startElement = createRouteMarkerElement('start');
                routeMarkers.current.start = new mapboxgl.Marker(startElement)
                  .setLngLat([startCoord.lng, startCoord.lat])
                  .addTo(map.current!);

                // Create finish marker
                const finishElement = createRouteMarkerElement('finish');
                routeMarkers.current.finish = new mapboxgl.Marker(finishElement)
                  .setLngLat([finishCoord.lng, finishCoord.lat])
                  .addTo(map.current!);

                console.log('Start and finish markers added to dashboard map');
              }
            }
          }
        } catch (error) {
          console.error('Error loading route coordinates:', error);
        }
      };

      loadRouteCoordinates();
    } else {
      console.log('LiveGPSMap: Not loading coordinates', {
        hasActiveRoute: !!stableRouteData,
        hasCoordinates: stableRouteData.hasCoordinates,
        stableRouteId
      });
    }
  }, [stableRouteId, stableRouteData]); // Only re-run when route ID or route data changes

  // Manual camera control functions
  const centerOnAllTrackers = () => {
    if (!map.current || trackers.length === 0) return;

    const validPositions = trackers
      .filter(t => t.position && t.position.lat && t.position.lng)
      .map(t => [t.position.lng, t.position.lat] as [number, number]);

    if (validPositions.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      validPositions.forEach(pos => bounds.extend(pos));

      if (validPositions.length === 1) {
        map.current.easeTo({
          center: validPositions[0],
          zoom: 15,
          duration: 1000
        });
      } else {
        map.current.fitBounds(bounds, {
          padding: 50,
          maxZoom: 15,
          duration: 1000
        });
      }
    }
  };

  const centerOnTracker = (trackerId: string) => {
    if (!map.current) return;

    const tracker = trackers.find(t => t.trackerId === trackerId);
    if (tracker && tracker.position) {
      map.current.easeTo({
        center: [tracker.position.lng, tracker.position.lat],
        zoom: Math.max(map.current.getZoom(), 15),
        duration: 1000
      });
    }
  };

  return (
    <Box sx={{ height, width: '100%', position: 'relative' }}>
      <div
        ref={mapContainer}
        style={{ height: '100%', width: '100%' }}
      />

      {/* Camera Controls Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        {/* Show/Hide Controls Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip title={showControls ? "Hide Camera Controls" : "Show Camera Controls"}>
            <IconButton
              onClick={() => setShowControls(!showControls)}
              sx={{
                backgroundColor: 'rgba(26, 26, 26, 0.9)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(26, 26, 26, 1)',
                  color: 'white'
                },
                boxShadow: 2
              }}
            >
              {showControls ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Camera Controls Panel */}
        {showControls && (
          <Card sx={{ minWidth: 280, backgroundColor: 'rgba(26, 26, 26, 0.95)', color: 'white' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle2" gutterBottom>
                Camera Controls
              </Typography>

              {/* Auto Camera Mode Toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={autoCameraMode}
                    onChange={(e) => setAutoCameraMode(e.target.checked)}
                    color="primary"
                  />
                }
                label="Auto Camera"
                sx={{ mb: 2, display: 'flex' }}
              />

              {/* Auto Mode Controls */}
              {autoCameraMode && (
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Follow Tracker</InputLabel>
                    <Select
                      value={followTracker}
                      onChange={(e) => setFollowTracker(e.target.value)}
                      label="Follow Tracker"
                      sx={{
                        color: 'white',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.5)'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.8)'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2'
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'rgba(255, 255, 255, 0.7)'
                        }
                      }}
                    >
                      <MenuItem value="all">All Trackers</MenuItem>
                      {trackers.map((tracker) => (
                        <MenuItem key={tracker.trackerId} value={tracker.trackerId}>
                          {tracker.title || tracker.currentRole}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {/* Manual Mode Controls */}
              {!autoCameraMode && (
                <Box>
                  <Typography variant="caption" color="rgba(255, 255, 255, 0.7)" display="block" sx={{ mb: 1 }}>
                    Manual Controls
                  </Typography>

                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FitScreen />}
                    onClick={centerOnAllTrackers}
                    fullWidth
                    sx={{
                      mb: 1,
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.8)',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    Fit All Trackers
                  </Button>

                  <ButtonGroup
                    orientation="vertical"
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiButton-root': {
                        color: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        '&:hover': {
                          borderColor: 'rgba(255, 255, 255, 0.8)',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }
                    }}
                  >
                    {trackers.map((tracker) => (
                      <Button
                        key={tracker.trackerId}
                        startIcon={<CenterFocusStrong />}
                        onClick={() => centerOnTracker(tracker.trackerId)}
                        sx={{ justifyContent: 'flex-start' }}
                      >
                        {tracker.title || tracker.currentRole}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};


export default LiveGPSMap;